/**
 * Multi-tenant context for AskWingo.
 *
 * Provides the current tenant and user to the entire app tree.
 * Hydrates from AuthContext (real user data from JWT / gateway response).
 */

import { useAuth } from '@/contexts/AuthContext';
import type { Tenant, TenantUser } from '@/types/tenant';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Context definition
// ---------------------------------------------------------------------------

interface TenantContextValue {
  currentTenant: Tenant;
  currentUser: TenantUser;
  setCurrentTenant: (tenant: Tenant) => void;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  // Build tenant/user from auth context
  const authTenant: Tenant = useMemo(
    () => ({
      id: user?.tenant_id ?? '',
      name: user?.tenant_name ?? '',
      slug: user?.tenant_slug ?? '',
      plan: 'professional' as const,
      createdAt: '',
    }),
    [user],
  );

  const authUser: TenantUser = useMemo(
    () => ({
      id: user?.user_id ?? '',
      email: user?.email ?? '',
      name: user?.display_name ?? '',
      role: (user?.role ?? 'member') as TenantUser['role'],
      tenantId: user?.tenant_id ?? '',
    }),
    [user],
  );

  const [tenantOverride, setTenantOverride] = useState<Tenant | null>(null);

  const currentTenant = tenantOverride ?? authTenant;
  const currentUser = authUser;

  const setCurrentTenant = useCallback((tenant: Tenant) => {
    setTenantOverride(tenant);
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({ currentTenant, currentUser, setCurrentTenant, isLoading: false }),
    [currentTenant, currentUser, setCurrentTenant],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useTenantContext = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenantContext must be used within a TenantProvider');
  }
  return ctx;
};
